import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { errorMessage, isApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useT, useTNode } from '../../lib/i18n'
import type { FieldErrors } from '../../lib/validate'
import { Button } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { Checkbox } from '../../components/ui/Checkbox'
import { Icon } from '../../components/ui/Icon'
import { InlineError } from '../../components/ui/ErrorState'
import { BootScreen } from '../../shell/ProtectedRoute'
import { AuthLayout } from './AuthLayout'
import { emailProblem, passwordProblem } from './validation'
import {
  PasswordlessNotice,
  SOCIAL_RETURN_KEYS,
  SocialReturnScreen,
  SocialSignIn,
  isPasswordlessAccountCode,
  readErrorProvider,
  readSocialReturn,
  safeReturnTo,
} from './social'
import type { SocialProvider } from './social'

type LoginField = 'email' | 'password'

interface LocationState {
  from?: string
}

/** The account has no password at all — a different situation from a wrong one. */
interface PasswordlessState {
  provider: SocialProvider | null
}

const Login = (): ReactElement => {
  const { login, status } = useAuth()
  const t = useT()
  const tn = useTNode()
  const navigate = useNavigate()
  const location = useLocation()
  const [params, setParams] = useSearchParams()

  // Seeded once, at mount: the collision screen hands the address back through the query string
  // so the person types their password, not their email, on the second attempt.
  const [email, setEmail] = useState(() => params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)
  const [errors, setErrors] = useState<FieldErrors<LoginField>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [passwordless, setPasswordless] = useState<PasswordlessState | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const state = location.state as LocationState | null
  const socialReturn = readSocialReturn(params)
  const destination =
    socialReturn?.kind === 'completing'
      ? socialReturn.returnTo
      : safeReturnTo(state?.from ?? null)

  /** Drops the callback's parameters, keeping the email so the form is already filled. */
  const dismissSocialReturn = (): void => {
    const next = new URLSearchParams(params)
    for (const key of SOCIAL_RETURN_KEYS) next.delete(key)
    const carried = params.get('email')
    if (carried) {
      setEmail(carried)
      next.set('email', carried)
    }
    setParams(next, { replace: true })
  }

  if (status === 'loading') return <BootScreen />
  if (status === 'authenticated') return <Navigate to={destination} replace />

  // The callback came back with something to say. It gets the whole card, not a banner.
  if (socialReturn) {
    return (
      <SocialReturnScreen
        outcome={socialReturn}
        returnTo={destination}
        onUsePassword={dismissSocialReturn}
      />
    )
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    const emailFault = emailProblem(email)
    const passwordFault = passwordProblem(password)
    setErrors({
      email: emailFault === null ? undefined : t(emailFault),
      password: passwordFault === null ? undefined : t(passwordFault),
    })
    setFormError(null)
    setPasswordless(null)
    if (emailFault !== null || passwordFault !== null) return

    setSubmitting(true)
    try {
      await login(email.trim(), password)
      await navigate(destination, { replace: true })
    } catch (error) {
      // "This account signs in with Google" is not a failed password — it is an account with no
      // password, and it gets its own notice naming the provider and offering the way in.
      if (isApiError(error) && isPasswordlessAccountCode(error.code)) {
        setPasswordless({ provider: readErrorProvider(error.details) })
      } else {
        setFormError(errorMessage(error))
      }
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title={t('common.actions.signIn')}
      subtitle={t('auth.login.subtitle')}
      footer={
        <p className="t-small text-ink-mute">
          {tn('auth.login.noAccount', {
            signup: (chunk) => (
              <Link
                to="/signup"
                className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
              >
                {chunk}
              </Link>
            ),
          })}
        </p>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          void onSubmit(event)
        }}
        className="flex flex-col gap-4"
      >
        {formError ? <InlineError>{formError}</InlineError> : null}
        {passwordless ? (
          <PasswordlessNotice provider={passwordless.provider} email={email.trim()} />
        ) : null}

        <Field label={t('auth.fields.email')} error={errors.email} required>
          <Input
            type="email"
            name="email"
            value={email}
            autoComplete="email"
            autoFocus
            placeholder={t('auth.fields.emailPlaceholder')}
            onChange={(event) => {
              setEmail(event.target.value)
            }}
          />
        </Field>

        <Field label={t('auth.fields.password')} error={errors.password} required>
          <Input
            type={reveal ? 'text' : 'password'}
            name="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => {
              setPassword(event.target.value)
            }}
            suffix={
              <button
                type="button"
                onClick={() => {
                  setReveal((previous) => !previous)
                }}
                aria-label={
                  reveal ? t('auth.fields.hidePassword') : t('auth.fields.showPassword')
                }
                className="ring-focus text-ink-mute transition-colors hover:text-ink"
              >
                <Icon name="eye" size={16} />
              </button>
            }
          />
        </Field>

        <div className="flex items-center justify-between gap-3">
          <Checkbox label={t('auth.login.remember')} defaultChecked name="remember" />
          <Link
            to="/forgot"
            className="t-small ring-focus text-ink-mute underline underline-offset-2 hover:text-ink"
          >
            {t('auth.login.forgot')}
          </Link>
        </div>

        <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
          {t('common.actions.signIn')}
        </Button>
      </form>

      <SocialSignIn returnTo={destination} className="mt-5" />
    </AuthLayout>
  )
}

export default Login
